import React, { useState } from 'react';
import { X, Edit, Car, User, Phone, Mail, Calendar, MapPin, Gauge, Fuel, Settings, AlertTriangle, Camera } from 'lucide-react';
import VehiclePhotosModal from './VehiclePhotosModal';
import { formatDate } from '../../utils/dateFormat';

const VehicleDetailsModal = ({ vehicle, customer, onClose, onEdit }) => {
  const [showPhotosModal, setShowPhotosModal] = useState(false);


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center">
            <Car className="w-5 h-5 mr-2" />
            Vehicle Details
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowPhotosModal(true)}
              className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
            >
              <Camera className="w-4 h-4 mr-1" />
              View Photos
            </button>
            <button
              onClick={onEdit}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Vehicle Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Car className="w-4 h-4 mr-2" />
              Vehicle Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make
                </label>
                <p className="text-gray-900 font-medium">{vehicle.make}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <p className="text-gray-900 font-medium">{vehicle.model}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plate Number
                </label>
                <p className="text-gray-900 font-medium">
                  {vehicle.plateNumber || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <p className="text-gray-900 font-medium">{vehicle.year || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <p className="text-gray-900 font-medium">{vehicle.color || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VIN Number
                </label>
                <p className="text-gray-900 font-mono text-sm">{vehicle.vin || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  vehicle.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                  vehicle.status === 'INACTIVE' ? 'bg-gray-100 text-gray-800' :
                  vehicle.status === 'MAINTENANCE' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {vehicle.status || 'ACTIVE'}
                </span>
              </div>
            </div>
          </div>

          {/* Technical Specifications */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              Technical Specifications
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Engine
                </label>
                <p className="text-gray-900">{vehicle.engine || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transmission
                </label>
                <p className="text-gray-900">{vehicle.transmission || 'Not specified'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuel Type
                </label>
                <div className="flex items-center">
                  <Fuel className="w-4 h-4 mr-2 text-gray-400" />
                  <p className="text-gray-900">{vehicle.fuelType || 'Not specified'}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mileage
                </label>
                <div className="flex items-center">
                  <Gauge className="w-4 h-4 mr-2 text-gray-400" />
                  <p className="text-gray-900">{vehicle.mileage ? `${vehicle.mileage.toLocaleString()} km` : 'Not specified'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Important Dates */}
          {(vehicle.insuranceExpiry || vehicle.registrationExpiry) && (
            <div className="bg-orange-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Important Dates
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vehicle.insuranceExpiry && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Insurance Expiry
                    </label>
                    <p className={`text-sm ${
                      new Date(vehicle.insuranceExpiry) < new Date() ? 'text-red-600 font-semibold' :
                      new Date(vehicle.insuranceExpiry) < new Date(Date.now() + 30*24*60*60*1000) ? 'text-orange-600 font-semibold' :
                      'text-gray-900'
                    }`}>
                      {formatDate(vehicle.insuranceExpiry)}
                      {new Date(vehicle.insuranceExpiry) < new Date() && ' (Expired)'}
                      {new Date(vehicle.insuranceExpiry) >= new Date() && new Date(vehicle.insuranceExpiry) < new Date(Date.now() + 30*24*60*60*1000) && ' (Expiring Soon)'}
                    </p>
                  </div>
                )}
                {vehicle.registrationExpiry && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Registration Expiry
                    </label>
                    <p className={`text-sm ${
                      new Date(vehicle.registrationExpiry) < new Date() ? 'text-red-600 font-semibold' :
                      new Date(vehicle.registrationExpiry) < new Date(Date.now() + 30*24*60*60*1000) ? 'text-orange-600 font-semibold' :
                      'text-gray-900'
                    }`}>
                      {formatDate(vehicle.registrationExpiry)}
                      {new Date(vehicle.registrationExpiry) < new Date() && ' (Expired)'}
                      {new Date(vehicle.registrationExpiry) >= new Date() && new Date(vehicle.registrationExpiry) < new Date(Date.now() + 30*24*60*60*1000) && ' (Expiring Soon)'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {vehicle.notes && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{vehicle.notes}</p>
            </div>
          )}

          {/* Customer Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <User className="w-4 h-4 mr-2" />
              Customer Information
            </h4>
            {customer ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <p className="text-gray-900 font-medium">{customer.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact
                  </label>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <p className="text-gray-900">{customer.contact || customer.phone || 'Not provided'}</p>
                  </div>
                </div>
                {customer.email && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      <p className="text-gray-900">{customer.email}</p>
                    </div>
                  </div>
                )}
                {customer.city && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                      <p className="text-gray-900">{customer.city}</p>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Type
                  </label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    customer.type === 'B2B' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {customer.type || 'B2C'}
                  </span>
                </div>
                {customer.feedback && customer.feedback !== 'UNRATED' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Feedback Rating
                    </label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      customer.feedback === 'EXCELLENT' ? 'bg-green-100 text-green-800' :
                      customer.feedback === 'GOOD' ? 'bg-blue-100 text-blue-800' :
                      customer.feedback === 'AVERAGE' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {customer.feedback}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic">No customer assigned to this vehicle</p>
            )}
          </div>



          {/* Timestamps */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Record Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created At
                </label>
                <p className="text-gray-600 text-sm">{formatDate(vehicle.createdAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Updated
                </label>
                <p className="text-gray-600 text-sm">{formatDate(vehicle.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            onClick={onEdit}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Edit Vehicle
          </button>
        </div>
      </div>
      
      {showPhotosModal && (
        <VehiclePhotosModal
          vehicle={vehicle}
          onClose={() => setShowPhotosModal(false)}
          allowDelete={false}
        />
      )}
    </div>
  );
};

export default VehicleDetailsModal;